import React, { createContext, useContext, useState, Dispatch, SetStateAction } from 'react'

// types
type searchContextValue = string;
type searchContextContainer = [searchContextValue];

// interfaces
interface SearchProviderProps {
    initialValue?: searchContextValue
}

// private interface describing context's hidden implementation
interface _SearchContextState {
    value: searchContextContainer
    setValue: Dispatch<SetStateAction<searchContextContainer>>
}

// public interface describing context's exposed implementation
export interface SearchContextState {
    value: searchContextValue
    setValue(value: string | (() => string)): void
}


// initial value is tuple of empty string
const Context = createContext<_SearchContextState>({ value: [""], setValue: () => { } });

// provider hides `value` in a tuple and allows user to interact like value is a string
// every time `value` is mutated, a new object is created. thus, every sibling component
// that uses one of the providers hooks, will be re-rendered when a mutation happens.
// this is contrary to how React.useState<T> behaves when T is a primitive.
//
// initial value is a string
export const SearchProvider: React.FC<SearchProviderProps> = ({ initialValue = "", children }) => {
    // encapsulate useState in SearchProvider
    // `useSearchContext` provides read-only updates
    // `useMutSearchContext` provides read-only and mutable access
    const [v, setV] = useState<searchContextContainer>([initialValue]);


    return (
        <Context.Provider value={{ value: v, setValue: setV }}>
            {children}
        </Context.Provider>
    )
}

export function useSearchContext(): searchContextValue {
    // provides read-only updates **only** when inner string is mutated
    // if value = "this"; setValue("this"); useSearchContext() will not cause rerender
    // use useSearchContextContainer if component needs to rerender every time
    // setValue is called
    const { value } = useContext(Context)
    return value[0]
}

export function useSearchContextContainer(): searchContextContainer {
    // provides read-only updates **every** time setValue is called
    // if value = "this"; setValue("this"); useSearchContext() **will cause** rerender
    // if component only needs to rerender when the internal string has been mutated,
    // use useSearchContext
    const { value } = useContext(Context)
    return value
}

export function useMutSearchContext(): SearchContextState {
    // provides read-only and mutable access
    const { value: v, setValue: setV } = useContext(Context)

    const setValue = (value: string | (() => string)) => {
        if (typeof value === 'function') {
            value = value()
        }
        setV([value])
    }

    return { value: v[0], setValue: setValue }

}

export default SearchProvider
